import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { colors } from '../components/Theme/WebTheme';
import { makeStyles } from '@mui/styles';
import MaterialTable from 'material-table';
import moment from 'moment/min/moment-with-locales';
import {
  calculateBookingToPickupTime,
  getBookingTimeAnalytics,
  formatDuration,
} from '../utils/bookingTimeUtils';
import { SECONDORY_COLOR, FONT_FAMILY } from '../common/sharedFunctions';
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { ThemeProvider } from '@mui/material/styles';
import theme from 'styles/tableStyle';
import CircularLoading from '../components/CircularLoading';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '20px',
    backgroundColor: colors.LandingPage_Background,
  },
  card: {
    borderRadius: '10px',
    backgroundColor: colors.WHITE,
    padding: '20px',
    marginBottom: '20px',
    boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
  },
  title: {
    color: colors.Header,
    fontFamily: FONT_FAMILY,
    fontWeight: 'bold',
    marginBottom: '20px',
  },
  metricCard: {
    textAlign: 'center',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '10px',
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: colors.WHITE,
    fontFamily: FONT_FAMILY,
  },
  metricLabel: {
    fontSize: '0.9rem',
    color: colors.WHITE,
    fontFamily: FONT_FAMILY,
    opacity: 0.9,
  },
  performanceCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 15px',
    marginBottom: '8px',
    borderRadius: '8px',
    backgroundColor: colors.CARD_DETAIL,
  },
  filterContainer: {
    marginBottom: '20px',
  },
}));

export default function BookingTimeAnalytics() {
  const { t } = useTranslation();
  const classes = useStyles();
  const bookinglistdata = useSelector(state => state.bookinglistdata);
  const auth = useSelector(state => state.auth);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (bookinglistdata.bookings) {
      let bookings = bookinglistdata.bookings.filter(booking => {
        // Filter by user type permissions
        if (auth.profile.usertype === 'fleetadmin') {
          return booking.fleetadmin === auth.profile.uid;
        }
        return auth.profile.usertype === 'admin';
      });

      // Apply time filter
      const now = new Date();
      if (timeFilter !== 'all') {
        bookings = bookings.filter(booking => {
          const bookingDate = new Date(booking.bookingDate);
          switch (timeFilter) {
            case 'today':
              return bookingDate.toDateString() === now.toDateString();
            case 'week':
              const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
              return bookingDate >= weekAgo;
            case 'month':
              const monthAgo = new Date(
                now.getTime() - 30 * 24 * 60 * 60 * 1000
              );
              return bookingDate >= monthAgo;
            default:
              return true;
          }
        });
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        bookings = bookings.filter(booking => {
          switch (statusFilter) {
            case 'completed':
              return ['STARTED', 'REACHED', 'COMPLETE'].includes(
                booking.status
              );
            case 'pending':
              return ['NEW', 'ACCEPTED', 'ARRIVED'].includes(booking.status);
            default:
              return true;
          }
        });
      }

      setFilteredBookings(bookings);
      setAnalytics(getBookingTimeAnalytics(bookings));
    }
  }, [bookinglistdata.bookings, auth.profile, timeFilter, statusFilter]);

  const columns = [
    {
      title: 'Booking Reference',
      field: 'reference',
      cellStyle: { fontFamily: FONT_FAMILY },
    },
    {
      title: 'Booking Date',
      field: 'bookingDate',
      render: rowData => moment(rowData.bookingDate).format('lll'),
      cellStyle: { fontFamily: FONT_FAMILY },
    },
    {
      title: 'Customer Name',
      field: 'customer_name',
      cellStyle: { fontFamily: FONT_FAMILY },
    },
    {
      title: 'Booking Status',
      field: 'status',
      render: rowData => (
        <Chip
          label={t(rowData.status)}
          style={{
            backgroundColor:
              rowData.status === 'CANCELLED'
                ? colors.RED
                : ['STARTED', 'REACHED', 'COMPLETE'].includes(rowData.status)
                  ? colors.GREEN
                  : colors.YELLOW,
            color: colors.WHITE,
            fontWeight: 'bold',
            fontFamily: FONT_FAMILY,
          }}
        />
      ),
    },
    {
      title: 'Time to Pickup',
      field: 'pickupTime',
      render: rowData => {
        const timeCalc = calculateBookingToPickupTime(rowData);
        return (
          <Chip
            label={timeCalc.displayValue}
            style={{
              backgroundColor: timeCalc.color || colors.BORDER_BACKGROUND,
              color:
                timeCalc.isPending || timeCalc.error
                  ? colors.BLACK
                  : colors.WHITE,
              fontWeight: 'bold',
              fontFamily: FONT_FAMILY,
            }}
          />
        );
      },
      cellStyle: { fontFamily: FONT_FAMILY },
    },
    {
      title: 'Performance',
      field: 'performance',
      render: rowData => {
        const timeCalc = calculateBookingToPickupTime(rowData);
        return timeCalc.performance || 'N/A';
      },
      cellStyle: { fontFamily: FONT_FAMILY },
    },
  ];

  if (!analytics) {
    return <CircularLoading />;
  }

  return (
    <div className={classes.container}>
      <Typography variant="h4" className={classes.title}>
        {'Booking to Pickup Analytics'}
      </Typography>

      {/* Filters */}
      <Card className={classes.card}>
        <Grid container spacing={2} className={classes.filterContainer}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>{'Time Period'}</InputLabel>
              <Select
                value={timeFilter}
                onChange={e => setTimeFilter(e.target.value)}
                style={{ fontFamily: FONT_FAMILY }}
              >
                <MenuItem value="all" style={{ fontFamily: FONT_FAMILY }}>
                  {'All Time'}
                </MenuItem>
                <MenuItem value="today" style={{ fontFamily: FONT_FAMILY }}>
                  {'Today'}
                </MenuItem>
                <MenuItem value="week" style={{ fontFamily: FONT_FAMILY }}>
                  {'This Week'}
                </MenuItem>
                <MenuItem value="month" style={{ fontFamily: FONT_FAMILY }}>
                  {'This Month'}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>{'Status'}</InputLabel>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{ fontFamily: FONT_FAMILY }}
              >
                <MenuItem value="all" style={{ fontFamily: FONT_FAMILY }}>
                  {'All Status'}
                </MenuItem>
                <MenuItem value="completed" style={{ fontFamily: FONT_FAMILY }}>
                  {'Completed'}
                </MenuItem>
                <MenuItem value="pending" style={{ fontFamily: FONT_FAMILY }}>
                  {'Pending'}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Key Metrics */}
      <Card className={classes.card}>
        <Typography
          variant="h6"
          style={{ marginBottom: '15px', fontFamily: FONT_FAMILY }}
        >
          Key Metrics
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              className={classes.metricCard}
              style={{ backgroundColor: colors.BLUE }}
            >
              <Typography className={classes.metricValue}>
                {analytics.totalBookings}
              </Typography>
              <Typography className={classes.metricLabel}>
                Total Bookings
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              className={classes.metricCard}
              style={{ backgroundColor: colors.GREEN }}
            >
              <Typography className={classes.metricValue}>
                {analytics.completedBookings}
              </Typography>
              <Typography className={classes.metricLabel}>
                Completed Pickups
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              className={classes.metricCard}
              style={{ backgroundColor: colors.VIOLET }}
            >
              <Typography className={classes.metricValue}>
                {formatDuration(analytics.averagePickupTime)}
              </Typography>
              <Typography className={classes.metricLabel}>
                Avg Pickup Time
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              className={classes.metricCard}
              style={{
                backgroundColor:
                  analytics.performancePercentage.excellent >= 70
                    ? colors.GREEN
                    : analytics.performancePercentage.excellent >= 50
                      ? colors.YELLOW
                      : colors.RED,
              }}
            >
              <Typography className={classes.metricValue}>
                {analytics.performancePercentage.excellent}%
              </Typography>
              <Typography className={classes.metricLabel}>
                Excellent (≤15min)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Card>

      {/* Performance Breakdown */}
      <Card className={classes.card}>
        <Typography
          variant="h6"
          style={{ marginBottom: '15px', fontFamily: FONT_FAMILY }}
        >
          Performance Breakdown
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <div
              className={classes.performanceCard}
              style={{ backgroundColor: colors.GREEN }}
            >
              <Typography
                style={{ color: colors.WHITE, fontFamily: FONT_FAMILY }}
              >
                Excellent (≤15min)
              </Typography>
              <Typography
                style={{
                  color: colors.WHITE,
                  fontWeight: 'bold',
                  fontFamily: FONT_FAMILY,
                }}
              >
                {analytics.performance.excellent} (
                {analytics.performancePercentage.excellent}%)
              </Typography>
            </div>
            <div
              className={classes.performanceCard}
              style={{ backgroundColor: colors.YELLOW }}
            >
              <Typography
                style={{ color: colors.BLACK, fontFamily: FONT_FAMILY }}
              >
                Good (16-30min)
              </Typography>
              <Typography
                style={{
                  color: colors.BLACK,
                  fontWeight: 'bold',
                  fontFamily: FONT_FAMILY,
                }}
              >
                {analytics.performance.good} (
                {analytics.performancePercentage.good}%)
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div
              className={classes.performanceCard}
              style={{ backgroundColor: '#ff914b' }}
            >
              <Typography
                style={{ color: colors.WHITE, fontFamily: FONT_FAMILY }}
              >
                Fair (31-60min)
              </Typography>
              <Typography
                style={{
                  color: colors.WHITE,
                  fontWeight: 'bold',
                  fontFamily: FONT_FAMILY,
                }}
              >
                {analytics.performance.fair} (
                {analytics.performancePercentage.fair}%)
              </Typography>
            </div>
            <div
              className={classes.performanceCard}
              style={{ backgroundColor: colors.RED }}
            >
              <Typography
                style={{ color: colors.WHITE, fontFamily: FONT_FAMILY }}
              >
                Poor (&gt;60min)
              </Typography>
              <Typography
                style={{
                  color: colors.WHITE,
                  fontWeight: 'bold',
                  fontFamily: FONT_FAMILY,
                }}
              >
                {analytics.performance.poor} (
                {analytics.performancePercentage.poor}%)
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Card>

      {/* Detailed Table */}
      <Card className={classes.card}>
        <Typography
          variant="h6"
          style={{ marginBottom: '15px', fontFamily: FONT_FAMILY }}
        >
          Detailed Breakdown
        </Typography>
        <ThemeProvider theme={theme}>
          <MaterialTable
            title=""
            columns={columns}
            data={filteredBookings}
            localization={localization(t)}
            options={{
              ...TableStyle().options,
              exportButton: true,
              exportAllData: true,
              exportCsv: (columns, data) => {
                const csvData = data.map(booking => ({
                  ...booking,
                  pickupTimeData: calculateBookingToPickupTime(booking),
                }));
                return {
                  filename: `booking-time-analytics-${moment().format('YYYY-MM-DD')}.csv`,
                  columns: [
                    ...columns,
                    {
                      title: 'Pickup Time (minutes)',
                      field: 'pickupTimeData.diffMins',
                    },
                  ],
                  data: csvData,
                };
              },
            }}
          />
        </ThemeProvider>
      </Card>
    </div>
  );
}
